import Axios from 'axios';

import { AppAPIName, UnAuthAPIName } from 'src/constants/apiNames';

// Structural slice of portal config. Importing the full PortalConfig type
// pulls the sidebar graph into switcher tests that mock `src/context`.
export type PortalSetupApiConfig = {
  AWS?: {
    API?: {
      endpoints?: { name: string; endpoint: string }[];
    };
  };
  BaseAPIUrl?: string;
};

/**
 * Resolve the API host for portal setup (`/portal/any/setup`).
 *
 * Prefer UnAuth (setup is unauthenticated), then BaseAPIUrl / AppAPI — those
 * follow CORE_API_URL / UN_AUTH_API_URL for local-private and LocalStack.
 * Do not use a hard-coded endpoints index: index 2 is AppBuilderStreamAPI and
 * can stay on staging via STREAM_API_URL while core points at localhost.
 */
export const getPortalSetupApiBaseUrl = (
  portalConfig: PortalSetupApiConfig,
): string => {
  const endpoints = portalConfig.AWS?.API?.endpoints ?? [];
  const byName = (name: string) =>
    endpoints.find((endpoint) => endpoint.name === name)?.endpoint;

  return (
    byName(UnAuthAPIName) || portalConfig.BaseAPIUrl || byName(AppAPIName) || ''
  );
};

export type AdditionalWorkspaceDecision = {
  allowed: boolean;
  portalId: string;
  portalUrl: string;
};

export const fetchAdditionalWorkspaceDecision = async (
  portalConfig: PortalSetupApiConfig,
  email: string,
): Promise<AdditionalWorkspaceDecision> => {
  const portalApiBaseURL = getPortalSetupApiBaseUrl(portalConfig);
  if (!portalApiBaseURL) {
    return { allowed: true, portalId: '', portalUrl: '' };
  }
  const response = await Axios.get<{
    allowed?: boolean;
    portalId?: string;
    portalUrl?: string;
  }>(`${portalApiBaseURL}/portal/any/additional-workspace`, {
    params: { email },
  });
  return {
    allowed: response.data.allowed === true,
    portalId:
      typeof response.data.portalId === 'string' ? response.data.portalId : '',
    portalUrl:
      typeof response.data.portalUrl === 'string'
        ? response.data.portalUrl
        : '',
  };
};

export type FreePortalEntry = {
  allowed: boolean;
  magicLink: string;
  email: string;
  portalId: string;
};

// The access token is the signup session that just confirmed the inbox.
// The server checks it before minting a one-time sign-in for the free workspace.
export const fetchFreePortalEntry = async (
  portalConfig: PortalSetupApiConfig,
  accessToken: string,
): Promise<FreePortalEntry | null> => {
  const portalApiBaseURL = getPortalSetupApiBaseUrl(portalConfig);
  if (!portalApiBaseURL || !accessToken) {
    return null;
  }
  const response = await Axios.post<{
    allowed?: boolean;
    magicLink?: string;
    email?: string;
    portalId?: string;
  }>(`${portalApiBaseURL}/portal/any/additional-workspace-entry`, null, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return {
    allowed: response.data.allowed === true,
    magicLink:
      typeof response.data.magicLink === 'string'
        ? response.data.magicLink
        : '',
    email: typeof response.data.email === 'string' ? response.data.email : '',
    portalId:
      typeof response.data.portalId === 'string' ? response.data.portalId : '',
  };
};

export const fetchAdditionalWorkspaceAllowed = async (
  portalConfig: PortalSetupApiConfig,
  email: string,
): Promise<boolean> => {
  const decision = await fetchAdditionalWorkspaceDecision(portalConfig, email);
  return decision.allowed;
};
